import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUserProfileStore } from 'stores/userProfileStore';
import {
  Loader2,
  User,
  Mail,
  Building2,
  ArrowRight,
  Calendar,
  Camera,
  Trash2,
  KeyRound,
} from 'lucide-react';
import Toast from 'components/Toast';
import {
  authorizedFetch,
  clientAuthorizedPatch,
  clientAuthorizedPost,
  clientAuthorizedDelete,
} from 'utils/fetchUtils';
import { API_ROUTES, ROUTES } from 'utils/constants';
import styles from './Profile.module.scss';

interface UploadUrlResponse {
  uploadUrl: string;
  path: string;
}

function Profile() {
  const navigate = useNavigate();
  const profile = useUserProfileStore((s) => s.profile);
  const isLoading = useUserProfileStore((s) => s.isLoading);
  const error = useUserProfileStore((s) => s.error);
  const refreshProfile = useUserProfileStore((s) => s.refreshProfile);
  const profilePictureUrl = useUserProfileStore((s) => s.profilePictureUrl);
  const getProfilePictureDownloadUrl = useUserProfileStore(
    (s) => s.getProfilePictureDownloadUrl,
  );
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [removingPicture, setRemovingPicture] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  useEffect(() => {
    if (profile?.profilePicturePath) {
      void getProfilePictureDownloadUrl();
    }
  }, [profile?.profilePicturePath, getProfilePictureDownloadUrl]);

  useEffect(() => {
    if (error) {
      showToast(error, 'error');
    }
  }, [error, showToast]);

  const handleAvatarClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || !file.type.startsWith('image/')) {
        showToast('Please select an image file.', 'error');
        return;
      }
      setUploadingPicture(true);
      try {
        const { uploadUrl, path } =
          await clientAuthorizedPost<UploadUrlResponse>(
            API_ROUTES.USER.PROFILE_PICTURE_UPLOAD_URL,
            {
              filename: file.name,
              contentType: file.type || 'image/jpeg',
            },
          );
        const putRes = await authorizedFetch(
          uploadUrl,
          {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type || 'image/jpeg' },
          },
          { skipAuth: true },
        );
        if (!putRes.ok) {
          throw new Error('Upload failed');
        }
        await clientAuthorizedPatch<{ profilePicturePath: string }>(
          API_ROUTES.USER.PROFILE_PICTURE,
          { path },
        );
        await refreshProfile(true);
        await getProfilePictureDownloadUrl();
        showToast('Profile picture updated.', 'success');
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to upload picture.';
        showToast(msg, 'error');
      } finally {
        setUploadingPicture(false);
      }
    },
    [showToast, refreshProfile, getProfilePictureDownloadUrl],
  );

  const handleRemovePicture = useCallback(async () => {
    if (!profile?.profilePicturePath) return;
    if (
      !window.confirm(
        'Remove your profile picture? You can upload a new one anytime.',
      )
    ) {
      return;
    }
    setRemovingPicture(true);
    try {
      await clientAuthorizedDelete<{ removed: boolean }>(
        API_ROUTES.USER.PROFILE_PICTURE,
      );
      await refreshProfile(true);
      showToast('Profile picture removed.', 'success');
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to remove picture.';
      showToast(msg, 'error');
    } finally {
      setRemovingPicture(false);
    }
  }, [profile?.profilePicturePath, refreshProfile, showToast]);

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) {
      return 'N/A';
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Loader2 className={styles.loadingIcon} size={48} />
          <p className={styles.loadingText}>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <p className={styles.errorMessage}>
            No profile found. Please complete your profile setup.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerRow}>
          <h1 className={styles.title}>User Profile</h1>
          <button
            type="button"
            className={styles.changePasswordButton}
            onClick={() => {
              void navigate(ROUTES.USER.CHANGE_PASSWORD);
            }}
          >
            <KeyRound size={18} aria-hidden />
            Change password
          </button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.profileCard}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className={styles.fileInput}
            aria-hidden
            onChange={(e) => {
              void handleFileChange(e);
            }}
          />
          <div className={styles.profileHeader}>
            <button
              type="button"
              className={styles.avatarButton}
              onClick={handleAvatarClick}
              disabled={uploadingPicture || removingPicture}
              aria-label="Change profile picture"
            >
              <div className={styles.avatar}>
                {profilePictureUrl ? (
                  <img
                    src={profilePictureUrl}
                    alt="Profile"
                    className={styles.avatarImage}
                  />
                ) : profile.username ? (
                  profile.username
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .substring(0, 2)
                ) : (
                  'U'
                )}
              </div>
              {(uploadingPicture || removingPicture) && (
                <div className={styles.avatarOverlay} aria-hidden>
                  <Loader2 className={styles.avatarOverlayIcon} size={24} />
                </div>
              )}
              {!uploadingPicture && !removingPicture && (
                <div className={styles.avatarEditHint}>
                  <Camera size={20} />
                </div>
              )}
            </button>
            <div className={styles.profileInfo}>
              <h2 className={styles.profileName}>{profile.username}</h2>
              {profile.email && (
                <p className={styles.profileEmail}>{profile.email}</p>
              )}
              {profile.profilePicturePath && (
                <button
                  type="button"
                  className={styles.removePictureButton}
                  onClick={() => void handleRemovePicture()}
                  disabled={removingPicture}
                  aria-label="Remove profile picture"
                >
                  <Trash2 size={16} />
                  <span>Remove picture</span>
                </button>
              )}
            </div>
          </div>

          <div className={styles.details}>
            <div className={styles.detailItem}>
              <div className={styles.detailIcon}>
                <User size={20} />
              </div>
              <div className={styles.detailContent}>
                <div className={styles.detailLabel}>Full Name</div>
                <div className={styles.detailValue}>{profile.username}</div>
              </div>
            </div>

            {profile.email && (
              <div className={styles.detailItem}>
                <div className={styles.detailIcon}>
                  <Mail size={20} />
                </div>
                <div className={styles.detailContent}>
                  <div className={styles.detailLabel}>Email</div>
                  <div className={styles.detailValue}>{profile.email}</div>
                </div>
              </div>
            )}

            {profile.companies && profile.companies.length > 0 ? (
              <div className={styles.detailItem}>
                <div className={styles.detailIcon}>
                  <Building2 size={20} />
                </div>
                <div className={styles.detailContent}>
                  <div className={styles.detailLabel}>Companies</div>
                  <div className={styles.detailValue}>
                    <div className={styles.companiesList}>
                      {profile.companies.map((company) => (
                        <div key={company.id} className={styles.companyItem}>
                          <span className={styles.companyName}>
                            {company.name}
                          </span>
                          {company.owned && (
                            <span className={styles.ownerBadge}>Owner</span>
                          )}
                        </div>
                      ))}
                    </div>
                    <Link
                      to={ROUTES.COMPANIES.ROOT}
                      className={styles.companiesLink}
                    >
                      <ArrowRight size={18} />
                      Go to Companies Page
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.detailItem}>
                <div className={styles.detailIcon}>
                  <Building2 size={20} />
                </div>
                <div className={styles.detailContent}>
                  <div className={styles.detailLabel}>Companies</div>
                  <div className={styles.detailValue}>
                    No companies assigned
                    <Link
                      to={ROUTES.COMPANIES.ROOT}
                      className={styles.companiesLink}
                    >
                      <ArrowRight size={18} />
                      Go to Companies Page
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {profile.createdAt && (
              <div className={styles.detailItem}>
                <div className={styles.detailIcon}>
                  <Calendar size={20} />
                </div>
                <div className={styles.detailContent}>
                  <div className={styles.detailLabel}>Member Since</div>
                  <div className={styles.detailValue}>
                    {formatDate(profile.createdAt)}
                  </div>
                </div>
              </div>
            )}

            {profile.updatedAt && (
              <div className={styles.detailItem}>
                <div className={styles.detailIcon}>
                  <Calendar size={20} />
                </div>
                <div className={styles.detailContent}>
                  <div className={styles.detailLabel}>Last Updated</div>
                  <div className={styles.detailValue}>
                    {formatDate(profile.updatedAt)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default Profile;
